/**
 * ノートの Markdown 書き出し（ダウンロード）ユーティリティ。
 *
 * インポート（ImportNotesModal: ファイル名 → タイトル）と対称に、
 * エクスポートは「タイトル → ファイル名」「本文（Markdown 文字列）→ .md」
 * とする。front matter 等の付加情報は持たない。
 */

import { MD_LINK_SOURCE } from "../../../components/markdown/markdownMedia";

/** OS のファイル名に使えない文字を「-」に置換し、前後の空白を除去する。 */
export const sanitizeFileName = (name: string): string => {
  const cleaned = name
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || "無題のノート";
};

/**
 * 重複しないファイル名（拡張子除く）を採番して返す（"名前", "名前 2", ...）。
 * `used` に採番済みの名前を蓄積する（呼び出し側でディレクトリ単位に管理する）。
 */
export const uniqueFileName = (base: string, used: Set<string>): string => {
  let candidate = base;
  let counter = 2;
  while (used.has(candidate)) {
    candidate = `${base} ${counter}`;
    counter += 1;
  }
  used.add(candidate);
  return candidate;
};

/**
 * 拡張子を保ったまま重複しないファイル名を採番する
 * （"a.png", "a 2.png", ...）。`used` に採番済みの名前を蓄積する。
 * 同梱ファイル（画像・添付ファイル）の命名に使う。
 */
export const uniqueAssetFileName = (
  fileName: string,
  used: Set<string>,
): string => {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot) : "";
  let candidate = fileName;
  let counter = 2;
  while (used.has(candidate)) {
    candidate = `${stem} ${counter}${ext}`;
    counter += 1;
  }
  used.add(candidate);
  return candidate;
};

/** md 本文からリンク先 URL を列挙する（image = 画像 / 通常リンクの別）。 */
const extractLinkUrls = (content: string, image: boolean): string[] => {
  const urls: string[] = [];
  const re = new RegExp(MD_LINK_SOURCE, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    // 先頭の `!` 有無で画像/通常リンクを判別する（lookbehind 非依存）
    if ((match[1] === "!") === image) urls.push(match[2]);
  }
  return urls;
};

/**
 * md 本文中の画像参照（`![alt](https://...)`）の URL を列挙する。
 * 相対パス等の非 http(s) 参照は対象にしない。
 */
export const extractImageUrls = (content: string): string[] =>
  extractLinkUrls(content, true);

/**
 * md 本文中の添付ファイル参照（画像でない通常リンク `[label](https://...)`）
 * の URL を列挙する。`![...](...)`（画像）は含めない。
 */
export const extractAttachmentUrls = (content: string): string[] =>
  extractLinkUrls(content, false);

/**
 * 本文中の画像/添付ファイルの URL を zip 内の相対パスへ書き換える。
 * `replacements`: URL → 相対パス（例: "images/foo.png" / "attachments/a.pdf"）。
 * 画像はインポート（ImportNotesModal の basename 一致置換）と往復できる形式。
 */
export const rewriteFileRefs = (
  content: string,
  replacements: Map<string, string>,
): string =>
  content.replace(
    /(!?\[(?:[^\]\\]|\\.)*\]\()(https?:\/\/[^)\s]+)(\))/g,
    (match, prefix: string, url: string, suffix: string) => {
      const path = replacements.get(url);
      return path ? `${prefix}${path}${suffix}` : match;
    },
  );

/** Blob をファイルとしてダウンロードする。 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/** ノート 1 件を `{タイトル}.md` としてダウンロードする。 */
export const downloadNoteAsMarkdown = (
  title: string,
  content: string,
): void => {
  const blob = new Blob([content ?? ""], {
    type: "text/markdown;charset=utf-8",
  });
  downloadBlob(blob, `${sanitizeFileName(title)}.md`);
};
